import { EDecision } from '@if/core/types'
import { formatCurrency } from '@if/core/utils'
import { Alert, Box, Card, CardContent, Divider, Stack, Typography } from '@mui/material'
import type { CalculatorEstimateResultDto } from '@src/data/api/calculator/calculator-api'
import { useCreditRefusalReasons } from '@src/data/selectors/app-config-selectors'
import type { NormsMessageKey } from '@src/data/types/constants'
import type { JSX, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import ResultRow from './ResultRow'

interface CalculatorEstimateSummaryProps {
  result: CalculatorEstimateResultDto
}

/** Literal keys so i18next keeps them typed. */
const DECISION_LABEL_KEYS = {
  [EDecision.Approved]: 'calculator.decisionValue.approved',
  [EDecision.Refused]: 'calculator.decisionValue.refused',
  [EDecision.Pending]: 'calculator.decisionValue.pending',
  [EDecision.Error]: 'calculator.decisionValue.error',
} as const

/**
 * The estimate's breakdown, sitting under the payment card in the aside: every figure grouped
 * under the heading it belongs to. The payment itself is `CalculatorPaymentDisplay`'s job.
 *
 * Every figure is computed server-side by the worksheet pipeline — nothing is recomputed or
 * rounded again on the client.
 */
const CalculatorEstimateSummary = ({ result }: CalculatorEstimateSummaryProps): JSX.Element => {
  const { t } = useTranslation()
  const creditRefusalReasons = useCreditRefusalReasons()

  const isApproved = result.decision === EDecision.Approved
  // A prequalification never returns manual-revision, so these four cover it.
  const decisionLabelKey = DECISION_LABEL_KEYS[result.decision as keyof typeof DECISION_LABEL_KEYS]

  // Same resolution the credit decision uses: the configured refusal-reason value list first,
  // falling back to the CreditDecisionNorms translations.
  const describeCode = (code: string) =>
    creditRefusalReasons.find((r) => r.id === code)?.text ?? t(`CreditDecisionNorms.${code as NormsMessageKey}`)

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2} divider={<Divider />}>
          <ResultSection title={t('calculator.rateSection')}>
            <ResultRow label={t('calculator.interestRate')} value={`${result.interestRate}%`} emphasis />
            <ResultRow label={t('calculator.annualPercentageRate')} value={`${result.effectiveRate}%`} />
            <ResultRow label={t('calculator.totalInterest')} value={formatCurrency(result.totalInterestAmount)} />
            <ResultRow label={t('calculator.pricedTerm')} value={t('calculator.termMonths', { count: result.term })} />
          </ResultSection>

          <ResultSection title={t('calculator.feesSection')}>
            <ResultRow
              label={t('calculator.lenderFee', { rate: result.lenderFeeRate })}
              value={formatCurrency(result.lenderFee)}
            />
            <ResultRow
              label={t('calculator.merchantFee', { rate: result.merchantFeeRate })}
              value={formatCurrency(result.merchantFee)}
            />
            <ResultRow label={t('calculator.protectionPlan')} value={formatCurrency(result.protectionPlanAmount)} />
          </ResultSection>

          <ResultSection title={t('calculator.prequalificationSection')}>
            <ResultRow
              label={t('calculator.decision')}
              value={decisionLabelKey ? t(decisionLabelKey) : result.decision}
              emphasis
            />
            <ResultRow label={t('calculator.maxLoanAmount')} value={formatCurrency(result.maxLoanAmount)} />
            <ResultRow
              label={t('calculator.maxLoanTerm')}
              value={t('calculator.termMonths', { count: result.maxLoanTerm })}
            />
          </ResultSection>

          {!isApproved && (
            <Alert severity="warning">
              <Typography variant="body2">{t('calculator.notApproved')}</Typography>
              <CodeList codes={result.refusalReasonCodes} describeCode={describeCode} />
            </Alert>
          )}

          {result.warningCodes.length > 0 && (
            <Alert severity="info">
              <CodeList codes={result.warningCodes} describeCode={describeCode} />
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}

const ResultSection = ({ title, children }: { title: string; children: ReactNode }): JSX.Element => (
  <Box>
    <Typography variant="overline" color="text.secondary">
      {title}
    </Typography>
    {children}
  </Box>
)

interface CodeListProps {
  codes: string[]
  describeCode: (code: string) => string
}

const CodeList = ({ codes, describeCode }: CodeListProps): JSX.Element | null =>
  codes.length === 0 ? null : (
    <Stack component="ul" sx={{ m: 0, pl: 2.5 }}>
      {codes.map((code) => (
        <Typography key={code} component="li" variant="caption">
          {describeCode(code)}
        </Typography>
      ))}
    </Stack>
  )

export default CalculatorEstimateSummary
